import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { Dialog, DialogContent, DialogTitle, toast } from 'design-system';

import ShareDialogContent from '@/features/_shared/ShareDialog/ShareDialogContent';
import { http } from '@/services/http';
import copyToClipboard from '@/shared/lib/copyToClipboard';
import { emailValidator } from '@/shared/lib/format/validators';

import type { ShareBlockNames } from '../../../types';
import type { WidgetActionState } from '../../AddToDashboardButton/useAddToDashboardAction';

type ShareBlockActionOptions = {
  activeTabName?: string;
  blockName: ShareBlockNames;
  shareTabParam: string;
};

export function useShareBlockAction({
  activeTabName,
  blockName,
  shareTabParam
}: ShareBlockActionOptions): Omit<WidgetActionState, 'disabled'> {
  const [modalOpen, setModalOpen] = useState(false);
  const [content, setContent] = useState('');
  const [recipient, setRecipient] = useState('');
  const [emailError, setEmailError] = useState('');
  const anchorRef = useRef<HTMLElement | null>(null);
  const previousModalOpenRef = useRef(false);

  const shareUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?element=${shareTabParam}`;
  const header =
    activeTabName === blockName
      ? `Share ${activeTabName}`
      : `Share ${activeTabName ?? ''} ${blockName}`.replace(/\s+/g, ' ').trim();

  const resetForm = useCallback(() => {
    setContent('');
    setEmailError('');
    setRecipient('');
  }, []);

  const openFrom = useCallback(
    (anchor: HTMLElement) => {
      anchorRef.current = anchor;
      resetForm();
      setModalOpen(true);
    },
    [resetForm]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setModalOpen(open);
      if (!open) resetForm();
    },
    [resetForm]
  );

  useLayoutEffect(() => {
    const wasOpen = previousModalOpenRef.current;
    previousModalOpenRef.current = modalOpen;

    if (wasOpen && !modalOpen) {
      anchorRef.current?.focus();
    }
  }, [modalOpen]);

  const onSendEmail = useCallback(async () => {
    const errorMessage = emailValidator(recipient);
    if (errorMessage) {
      setEmailError(errorMessage);
      return;
    }

    try {
      toast.add({ title: 'Email is sending, please wait...', type: 'loading' });
      setModalOpen(false);
      await http.go.post('/share', {
        content,
        recipient,
        url: shareUrl
      });
      toast.add({ title: 'Email sent!', type: 'success', timeout: 5000 });
    } catch {
      toast.add({ title: 'Something went wrong!', type: 'error', timeout: 5000 });
    }
  }, [content, recipient, shareUrl]);

  const onCopyClick = useCallback(() => {
    void copyToClipboard(shareUrl, 'Link copied to clipboard!');
  }, [shareUrl]);

  return {
    openFrom,
    layer: (
      <Dialog
        open={modalOpen}
        onOpenChange={handleOpenChange}
      >
        <DialogContent className='sm:max-w-xl'>
          <DialogTitle>{header}</DialogTitle>
          <ShareDialogContent
            content={content}
            emailError={emailError}
            handleChangeRecipient={event => {
              setEmailError('');
              setRecipient(event.target.value);
            }}
            header={header}
            recipient={recipient}
            shareUrl={shareUrl}
            showHeader={false}
            onContentChange={event => {
              setContent(event.target.value);
            }}
            onCopyClick={onCopyClick}
            onSendEmail={onSendEmail}
          />
        </DialogContent>
      </Dialog>
    )
  };
}
